import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { createStructuredSelector } from 'reselect/lib';
import queryString from 'query-string';
import { injectIntl } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import isArray from 'lodash/isArray';
import { PaginationBar } from 'apex-table-ts';

import {
  selectResults as selectSearchResults, searchRequest, TRANSFER_STATES, TRANSFER_REVIEW_ROLES, searchParams, previousPageToken,
  nextPageToken, totalSize, selectSearchShowAllFields,
} from 'services/search';
import SearchForm, { TRANSFER_STATE_MUTLISELECT_OPTIONS } from 'components/SearchForm';
import SearchResultsTable from 'components/SearchResultsTable';
import TransferStateDescriptionModal from 'components/TransferStateDescriptionModal';
import { selectIsSearchLoading, selectFilteredResultsCount } from './selectors';
import { setFilteredResultsCount } from './actions';
import styles from './styles.css';
import messages from './messages';
export const { title } = messages;


export class Search extends Component {
  static propTypes = {
    intl: PropTypes.object.isRequired,
    isSearchLoading: PropTypes.bool.isRequired,
    searchResults: PropTypes.array,
    previousPageToken: PropTypes.string,
    totalSize: PropTypes.number,
    searchParams: PropTypes.object,
    nextPageToken: PropTypes.string,
    searchRequest: PropTypes.func.isRequired,
    setFilteredResultsCount: PropTypes.func.isRequired,
    location: PropTypes.shape({
      pathName: PropTypes.string,
      search: PropTypes.string,
    }).isRequired,
    history: PropTypes.object.isRequired,
    hasFullTransfersPermission: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    searchResults: [],
    searchParams: {
      state: [''],
      view_option: '',
      include_total_size: true,
    },
    previousPageToken: null,
    nextPageToken: null,
    totalSize: 0,
  };

  constructor(props) {
    super(props);

    this.handleSearchFormSubmit = this.handleSearchFormSubmit.bind(this);
    this.performInitialSearchIfAny = this.performInitialSearchIfAny.bind(this);
    this.updateLocationSearch = this.updateLocationSearch.bind(this);
    this.showTransferStateDescriptionModal = this.showTransferStateDescriptionModal.bind(this);
    this.hideTransferStateDescriptionModal = this.hideTransferStateDescriptionModal.bind(this);
    this.handlePreviousPage = this.handlePreviousPage.bind(this);
    this.handleNextPage = this.handleNextPage.bind(this);
    this.handleResetInitialFormValues = this.handleResetInitialFormValues.bind(this);

    this.state = {
      showTransferStateDescriptionModal: false,
      pageToken: null,
    };
  }

  componentDidMount() {
    this.performInitialSearchIfAny();
  }

  resolvePageToken(tokenFromUrl, tokenFromState) {
    if (tokenFromState) {
      return tokenFromState;
    }
    if (tokenFromUrl) {
      return tokenFromUrl;
    }
    return null;
  }

  /**
   * Parses queryParams from location,
   * If queryParams exist, the form is populated
   * and a search is executed using the parsed params.
   */
  /* eslint-disable camelcase */
  performInitialSearchIfAny() {
    let valuesFromUrlBar;
    const { search } = this.props.location;
    const { hasFullTransfersPermission } = this.props;

    if (search) {
      const {
        apex_account,
        direction,
        start_date,
        end_date,
        transfer_id,
        state,
        correspondent,
        view_option,
        transfer_type,
        fed_wire_reference_number,
        house_bank_account_number,
        page_token,
        max_amount,
        min_amount,
      } = queryString.parse(search, { arrayFormat: 'comma' });
      valuesFromUrlBar = {
        apex_account,
        direction,
        start_date,
        end_date,
        transfer_id,
        state,
        correspondent,
        view_option: hasFullTransfersPermission && view_option,
        transfer_type,
        fed_wire_reference_number,
        house_bank_account_number,
        page_token: this.resolvePageToken(page_token, this.state.pageToken),
        include_total_size: 'true',
        max_amount,
        min_amount,
      };
    } else {
      // Apply default form values if nothing was saved to url bar.
      valuesFromUrlBar = {

        state: [TRANSFER_STATES.transferStateReviewPending],
        view_option: TRANSFER_REVIEW_ROLES.transferReviewRoleMyQueue,
        ...(this.state.pageToken && { page_token: this.state.pageToken }),
        include_total_size: 'true',
      };
    }
    const initialFormValues = { ...valuesFromUrlBar };
    // Transform "state" values to something that makes sense to the search form MultiSelect options prop.
    if (valuesFromUrlBar && isArray(valuesFromUrlBar.state)) {
      initialFormValues.state = initialFormValues.state.map((value) => (
        TRANSFER_STATE_MUTLISELECT_OPTIONS.find((option) => option.value === value)));
    }

    // If only one state was saved in URL bar, queryString.parse treats it as a string, not an array.
    // Let's make it an array again.
    if (valuesFromUrlBar && isString(valuesFromUrlBar.state)) {
      initialFormValues.state = [
        TRANSFER_STATE_MUTLISELECT_OPTIONS.find((option) => option.value === valuesFromUrlBar.state),
      ];
    }

    // populate the form
    this.setState({
      initialFormValues,
    }, () => {
      // execute the search
      // const params = { ...valuesFromUrlBar, currentPageToken: this.state.pageToken, test: 'test' };
      this.props.searchRequest(valuesFromUrlBar);
    });
  }

  /**
   * Update the URL bar queryParams (AKA location.search)
   * using history prop provided by react-router.
   */
  updateLocationSearch(values) {
    const search = queryString.stringify(values, { arrayFormat: 'comma' });
    const { pathName } = this.props.location;
    this.props.history.push({ pathName, search });
  }

  updateLocationPagination(token) {
    const { history } = this.props;

    const queryArray = Object.entries(this.props.searchParams).map((param) => {
      if (param[1] && param[0] !== 'page_token') {
        return `${'&'}${param[0]}=${param[1]}`;
      }
      return '';
    });

    const queryStringWithToken = `?${queryArray.join('').substring(1)}&page_token=${token}`;
    return history.push(queryStringWithToken);
  }

  handleSearchFormSubmit(values = {}) {
    const { page_token, ...payload } = values;
    if (isArray(values.state) && !isEmpty(values.state)) {
      payload.state = values.state.map((state) => state.value);
    }
    this.updateLocationSearch(payload);
    this.props.searchRequest(payload);
  }

  showTransferStateDescriptionModal() {
    this.setState({ showTransferStateDescriptionModal: true });
  }

  hideTransferStateDescriptionModal() {
    this.setState({ showTransferStateDescriptionModal: false });
  }
  handlePreviousPage() {
    this.setState({ pageToken: this.props.previousPageToken }, () => {
      this.updateLocationPagination(this.props.previousPageToken);
      this.performInitialSearchIfAny();
    });
  }
  handleNextPage() {
    this.setState({ pageToken: this.props.nextPageToken }, () => {
      this.updateLocationPagination(this.props.nextPageToken);
      this.performInitialSearchIfAny();
    });
  }
  handleResetInitialFormValues() {
    const initialFormValues = {
      apex_account: null,
      direction: null,
      start_date: null,
      end_date: null,
      transfer_id: null,
      state: null,
      correspondent: null,
      transfer_type: null,
      fed_wire_reference_number: null,
      house_bank_account_number: null,
      page_token: this.state.pageToken || null,
      max_amount: null,
      min_amount: null,
      include_total_size: 'true',
    };
    this.setState({ initialFormValues });
  }

  render() {
    const {
      isSearchLoading,
      intl,
      searchResults,
      hasFullTransfersPermission,
    } = this.props;
    return (
      <div className={styles.search}>
        <header>
          <SearchForm
            isSearchLoading={isSearchLoading}
            initialValues={{ ...this.state.initialFormValues }}
            onTransferStateInformationIconClicked={this.showTransferStateDescriptionModal}
            onSubmit={this.handleSearchFormSubmit}
            showAllFields={hasFullTransfersPermission}
            resetFormInitialValues={this.handleResetInitialFormValues}
          />
        </header>
        <SearchResultsTable
          loading={isSearchLoading}
          intl={intl}
          rows={searchResults}
          onFilteredRowCountChange={this.props.setFilteredResultsCount}
          showAllFields={hasFullTransfersPermission}
        />
        <PaginationBar itemCount={this.props.totalSize} onNextPage={this.handleNextPage} onPreviousPage={this.handlePreviousPage} showNextPageButton={this.props.nextPageToken} showPreviousPageButton={this.props.previousPageToken} />
        <TransferStateDescriptionModal
          show={this.state.showTransferStateDescriptionModal}
          onHide={this.hideTransferStateDescriptionModal}
        />
      </div>
    );
  }
}

/* istanbul ignore next */
const mapStateToProps = (/*state*/) => createStructuredSelector({
  isSearchLoading: selectIsSearchLoading,
  searchResults: selectSearchResults,
  searchParams,
  previousPageToken,
  nextPageToken,
  totalSize,
  hasFullTransfersPermission: selectSearchShowAllFields,
});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch) => bindActionCreators({
  setFilteredResultsCount,
  searchRequest,
}, dispatch);

export { selectFilteredResultsCount };
export { default as reducer } from './reducer';
export { NAMESPACE } from './constants';

Search.displayName = 'Search Container';
export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(Search));



// WEBPACK FOOTER //
// ./src/containers/Search/index.js